<template>
  <a-modal
    v-model:open="open"
    class="p-0"
    :closable="false"
    :body-style="{ padding: '0px' }"
    destroy-on-close
    @cancel="close"
  >
    <div class="flex flex-col gap-y-3">
      <div>
        <a-input
          v-model:value="search"
          class="border border-cyan-600 border-solid"
          placeholder="输入工厂名称进行过滤"
          size="large"
          allow-clear
        >
          <template #prefix>
            <search-outlined />
          </template>
        </a-input>
      </div>
      <div class="max-h-120 select-none overflow-y-auto">
        <div class="divide-x-0 divide-y-1 divide-gray-300 divide-solid">
          <template v-if="!filteredFactories.length">
            <a-empty>
              <template #description>
                <span class="text-base font-bold text-yellow-700">未找到匹配工厂名称</span>
              </template>
            </a-empty>
          </template>
          <template v-else>
            <div
              v-for="factory in filteredFactories"
              :key="factory.id"
              class="flex flex-row items-center justify-between px-3 py-3 text-base hover:bg-gray-100 hover:text-cyan-800"
            >
              <div class="flex items-center gap-x-3">
                <icon-vue icon="material-symbols:factory-rounded" class="text-3xl text-blue-500" />
                <div class="flex items-center gap-x-3">
                  <span class="truncate max-w-xs" :title="factory.name">
                    {{ factory.name }}
                  </span>
                </div>
              </div>

              <a-button class="flex items-center gap-x-0.5 text-sm" type="link" @click="selectFactory(factory)">
                <span>选择</span>
                <icon-vue icon="mingcute:right-fill" />
              </a-button>
            </div>
          </template>
        </div>
      </div>
    </div>
    <template #footer>
      <div class="text-gray">点击“选择”，展示对应的所属组织数据。</div>
    </template>
  </a-modal>
</template>

<script setup lang="ts">
import { useUserStore } from '@/store';
import { getAccessibleFactories, setFactoryCache } from '@/utils/factory';
import { SearchOutlined } from '@ant-design/icons-vue';
import { Icon as IconVue } from '@iconify/vue';
import { Button as AButton, Empty as AEmpty, Input as AInput, Modal as AModal } from 'ant-design-vue';
import { computed, onMounted, ref } from 'vue';
import type { Factory } from './config';

const open = ref(false);
const search = ref('');
const userStore = useUserStore();
const accessibleFactories = ref<Factory[]>([]);
const loading = ref(false);

const emit = defineEmits(['close', 'select']);

const close = () => {
  open.value = false;
  emit('close');
};

const selectFactory = (factory: Factory) => {
  emit('select', factory);
  setFactoryCache(factory);
  close();
};

const show = async () => {
  open.value = true;
  await loadFactories();
};

// 加载可访问的工厂列表
const loadFactories = async () => {
  loading.value = true;
  try {
    accessibleFactories.value = await getAccessibleFactories(userStore.current);
  } catch (error) {
    // 如果获取失败，使用默认值
    const { factoryList } = await import('@/utils/factory');
    accessibleFactories.value = factoryList;
  } finally {
    loading.value = false;
  }
};

const filteredFactories = computed(() => {
  return accessibleFactories.value.filter((factory) => factory.name.toLowerCase().includes(search.value.toLowerCase()));
});

// 组件挂载时预加载工厂列表
onMounted(() => {
  loadFactories();
});

defineExpose({ show });
</script>

<style scoped>
.ant-modal-content {
  padding: 24px;
}
</style>
